import { ChangeDetectionStrategy, Component, computed, inject, model, OnDestroy, ViewEncapsulation } from '@angular/core';
import { TopNavigationItem } from '@core/navigation/models/top-navigation/top-navigation-item.interface';
import { SpinnerComponent, toggleContentFadeIn, toggleContentFadeOut } from '@kaseya-design-components';
import { SsoTargetPage } from '@design/open-sso-link/models/sso-target-page.enum';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { NavigationFacade } from '@core/navigation/facades/navigation.facade';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { AccountTeamPanelComponent } from '@layout/account-team/components/account-team-panel/account-team-panel.component';
import { LeftNavigationComponent } from '@layout/navigation/components/left-navigation/left-navigation.component';
import { asNonUndefined } from '@kaseya-utils';
import { PermissionDirective } from '@core/permissions/directives/permission.directive';
import { OpenSsoLinkDirective } from '@design/open-sso-link/directives/open-sso-link.directive';
import { Permission } from '@kaseya-shared';
import { getHeaderHeight } from '@shared/utils/header-height';
import { FeaturesDirective } from '@core/features/directives/features.directive';
import { NavigationLinkComponent } from '@layout/navigation/components/navigation-link/navigation-link.component';

@Component({
  selector: 'itc-navigation-menu-panel',
  templateUrl: 'navigation-menu-panel.component.html',
  styleUrl: 'navigation-menu-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [toggleContentFadeIn, toggleContentFadeOut],
  imports: [
    AsyncPipe,
    SpinnerComponent,
    TranslatePipe,
    AccountTeamPanelComponent,
    LeftNavigationComponent,
    PermissionDirective,
    OpenSsoLinkDirective,
    FeaturesDirective,
    NavigationLinkComponent,
  ],
})
export class NavigationMenuPanelComponent implements OnDestroy {
  private readonly navigationFacade = inject(NavigationFacade);

  readonly isOpen = model<boolean>();
  readonly topNavigationItems = computed(() => [
    ...this.navigationFacade.topNavigationItems(),
    ...this.navigationFacade.settingsNavigationItems(),
  ]);
  readonly activeTopNavigation = this.navigationFacade.activeTopNavigation;
  readonly TopNavigationPath = TopNavigationPath;
  readonly Permission = Permission;
  readonly SsoTargetPage = SsoTargetPage;
  readonly headerHeight = getHeaderHeight();
  readonly isKaseyaStoreSsoLinkLoading$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.expandActiveTopNavigationItem();
  }

  toggleNavigationItem(item: TopNavigationItem): void {
    item.isCollapsed?.update(isCollapsed => !isCollapsed);
  }

  ngOnDestroy(): void {
    this.navigationFacade.resetTopNavigationItemsCollapsedState();
  }

  private expandActiveTopNavigationItem(): void {
    const activeItem = this.topNavigationItems().find(({ id }) => this.activeTopNavigation() === id);

    if (activeItem) {
      asNonUndefined(activeItem.isCollapsed).set(false);
    }
  }
}
